import { useMemo, useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { Link } from 'react-router';
import { Pencil, Users } from 'lucide-react';

import { usePatchProject, useSetTeamPositions } from '../api/planning-hooks';
import { PROJECT_PRIORITIES, PROJECT_PRIORITY_LABELS } from '../api/types';
import { statusLabel } from '../lib/statuses';
import type { Priority, Project, RegistryDocument, Team } from '../api/types';
import { isOverdue, teamCapacity } from '../lib/capacity';
import type { StatusReport } from '../lib/capacity';
import { orderedIds, sortTeamQueueManual } from '../lib/manual-order';
import { DEFAULT_QUEUE_SORT, QUEUE_SORT_KEYS, QUEUE_SORT_LABELS, sortTeamQueueWith } from '../lib/queue-order';
import type { QueueSortKey } from '../lib/queue-order';
import { formatDisplayDate } from '../lib/date';
import { projectWord } from '../lib/plural';
import { matchesFilters } from '../lib/filters';
import { priorityRowClass } from '../lib/priority-class';
import { INTERACTIVE, manualRowDragProps, useManualOrder } from '../hooks/useManualOrder';
import { ProjectEditModal } from '../components/ProjectEditModal';
import { initials } from '../components/PersonPickerModal';
import { DragHandleCell } from '../components/DragHandleCell';
import { RegistryGate } from '../components/RegistryGate';
import { SummaryChips } from '../components/SummaryChips';
import { FilterBar } from '../components/FilterBar';
import { PageHeader, FilteredEmptyState } from '../components/view-parts';
import { Badge } from '../components/ui';
import { useFilters, useRegistryFilterContext } from '../state/FiltersProvider';

/**
 * F-19b: «Команды» — members, team capacity over UNIQUE projects (F-03, no
 * double counting) and the queue. The queue's default order is «Вручную» —
 * `teamPosition` over the team's assignments, arranged by dragging; the other
 * sort modes keep the `teamPriority` ranking (queue №3). `teamPriority` stays
 * editable right here — it is the coarse field the manual order sits on top of.
 * The project-count chip links to `/projects?team=<id>` (F-10).
 */
export function TeamsPage(): ReactElement {
  return <RegistryGate label="Загрузка реестра">{(registry) => <TeamsView registry={registry} />}</RegistryGate>;
}

function TeamsView({ registry }: { registry: RegistryDocument }): ReactElement {
  const { filters, isActive } = useFilters();
  const { maps } = useRegistryFilterContext();

  // Edit by ID (see ProjectsPage): a healed cache after a 409 re-opens the
  // dialog on the fresh project, never on a captured stale row.
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const editing = registry.projects.find((project) => project.id === editingId);

  // The team filter (F-19, shared FilterBar) leaves only the chosen team(s).
  const teams = useMemo(() => {
    const selected = filters.teamId ?? [];
    return selected.length > 0 ? registry.teams.filter((team) => selected.includes(team.id)) : registry.teams;
  }, [registry.teams, filters.teamId]);

  return (
    <>
      <PageHeader
        title="Команды"
        sub="Очередь №3: «Вручную» — порядок ростера перетаскиванием, остальные режимы — по teamPriority; загрузка по уникальным проектам (F-03)."
      />

      <SummaryChips />

      <FilterBar />

      {teams.map((team) => (
        <TeamCard
          key={team.id}
          team={team}
          registry={registry}
          maps={maps}
          filters={filters}
          filtersActive={isActive}
          onEdit={setEditingId}
        />
      ))}

      {/* Owner feedback #2: active filters that leave no team at all hide the
          block — real emptiness without filters keeps its message. */}
      {teams.length === 0 ? (
        isActive ? null : (
          <section className="card">
            <FilteredEmptyState what="команды" whatGenitive="команд" />
          </section>
        )
      ) : null}

      {editing ? <ProjectEditModal open onClose={() => setEditingId(undefined)} registry={registry} project={editing} /> : null}
    </>
  );
}

function TeamCard({
  team,
  registry,
  maps,
  filters,
  filtersActive,
  onEdit,
}: {
  team: Team;
  registry: RegistryDocument;
  maps: Parameters<typeof matchesFilters>[2] | null;
  filters: Parameters<typeof matchesFilters>[1];
  filtersActive: boolean;
  /** Opens the project card (row click / title pencil → the shared dialog). */
  onEdit: (projectId: string) => void;
}): ReactElement {
  const patch = usePatchProject();
  const setTeamPositions = useSetTeamPositions();
  const [sort, setSort] = useState<QueueSortKey>(DEFAULT_QUEUE_SORT);

  const members = useMemo(
    () =>
      registry.people
        .filter((person) => person.teamId === team.id)
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [registry.people, team.id],
  );
  const memberIds = useMemo(() => new Set(members.map((member) => member.id)), [members]);
  const namesById = useMemo(() => new Map(registry.people.map((person) => [person.id, person.name])), [registry.people]);

  /**
   * Row click = open the project card (owner request, «Команды»). Clicks on an
   * interactive control (select/input/button/link) belong to that control —
   * the same guard the row drag uses. Left button only.
   */
  const openFromRow = (project: Project) => (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement | null)?.closest(INTERACTIVE)) return;
    onEdit(project.id);
  };

  /** The project's assignees WHO BELONG TO THIS TEAM (the roster's own people). */
  const teamAssignees = (project: Project): string[] =>
    project.assignments.filter((assignment) => memberIds.has(assignment.personId)).map((assignment) => namesById.get(assignment.personId) ?? assignment.personId);

  const visible = useMemo(() => {
    if (!maps) return [];
    return registry.projects.filter((project) => matchesFilters(project, filters, maps));
  }, [registry.projects, filters, maps]);

  const queue = useMemo(
    () => sortTeamQueueWith(team.id, visible, registry.people, sort),
    [visible, registry.people, team.id, sort],
  );

  const capacity: StatusReport = useMemo(
    () => teamCapacity(team.id, visible, registry.people),
    [visible, registry.people, team.id],
  );

  /**
   * One reorder = optimistic cache update + `POST /registry/team-positions`
   * with the FULL roster; the hook swaps in the server document (or heals the
   * cache on a 409). `null` outcomes (same row, edge) are silently ignored.
   */
  const applyOrder = (nextIds: readonly string[] | null) => {
    if (!nextIds || nextIds.length === 0) return;
    setTeamPositions.mutate({ version: registry.version, teamId: team.id, orderedProjectIds: [...nextIds] });
  };

  /**
   * The drag scope is the team's WHOLE roster, computed from the UNFILTERED
   * registry: the wire op (`POST /registry/team-positions`) demands the FULL
   * order, and posting only the visible subset is `400 invalid` (prod bug
   * 2026-10-26, the /people twin). `queue` above stays the display-only list —
   * global filters narrow the rows shown, never the posted list (same model as
   * StreamSection and F-28 on «Все проекты»); the order here is raw project
   * ids. A drop puts the dragged id at the target's slot in the FULL roster —
   * filters do not shift positions — and a keyboard nudge whose neighbour row
   * is filtered out looks like a no-op (the already-shipped convention on
   * «Потоки» / «Все проекты»).
   */
  const fullOrder = useMemo(
    () => orderedIds(sortTeamQueueManual(team.id, registry.projects, registry.people)),
    [registry.projects, registry.people, team.id],
  );

  const controls = useManualOrder({
    enabled: sort === 'manual',
    busy: setTeamPositions.isPending,
    order: fullOrder,
    onApply: applyOrder,
  });

  const setTeamPriority = (project: Project, priority: Priority) => {
    const assignments = project.assignments.map((assignment) =>
      memberIds.has(assignment.personId) ? { ...assignment, teamPriority: priority } : assignment,
    );
    patch.mutate({ id: project.id, body: { version: registry.version, set: {}, assignments: { set: assignments } } });
  };

  return (
    <section
      className={`card card--flush team-card${controls.enabled ? ' team-card--manual' : ''}`}
      aria-label={`Команда: ${team.name}`}
      data-testid={`team-${team.id}`}
    >
      <header className="team-card__header">
        <div className="team-card__who">
          <h2 className="team-card__name">{team.name}</h2>
          {team.description ? <p className="page-header__sub">{team.description}</p> : null}
        </div>

        <Link
          className="badge badge--brand team-card__count"
          to={`/projects?team=${encodeURIComponent(team.id)}`}
          title="Открыть проекты команды во «Все проекты»"
        >
          {queue.length} {projectWord(queue.length)}
        </Link>

        <div className="stream-section__tools">
          <label className="toolbar__label" htmlFor={`team-${team.id}-sort`}>
            Сортировка
          </label>
          <select
            id={`team-${team.id}-sort`}
            className="input input--compact"
            value={sort}
            onChange={(event) => setSort(event.target.value as QueueSortKey)}
          >
            {QUEUE_SORT_KEYS.map((key) => (
              <option key={key} value={key}>
                {QUEUE_SORT_LABELS[key]}
              </option>
            ))}
          </select>
        </div>

        <dl className="capacity">
          <div className="capacity__item capacity__item--total">
            <dt>Загрузка</dt>
            <dd>
              <span className="capacity__num">{capacity.hours}</span> ч
            </dd>
          </div>
          <div className="capacity__item">
            <dt>Согласовано</dt>
            <dd>
              <span className="capacity__num">{capacity.hoursByStatus.agreed}</span> ч
            </dd>
          </div>
          <div className="capacity__item">
            <dt>В работе</dt>
            <dd>
              <span className="capacity__num">{capacity.hoursByStatus.in_work}</span> ч
            </dd>
          </div>
          <div className="capacity__item">
            <dt>Просрочено</dt>
            <dd className={capacity.overdue > 0 ? 'capacity__overdue' : undefined}>
              <span className="capacity__num">{capacity.overdue}</span>
            </dd>
          </div>
        </dl>
      </header>

      <div className="team-card__members" aria-label={`Участники команды ${team.name}`}>
        <Users size={14} aria-hidden="true" />
        {members.map((member) => (
          <span key={member.id} className="assignee-chip" title={member.role ? `${member.name} — ${member.role}` : member.name}>
            <span className="assignee-chip__avatar" aria-hidden="true">
              {initials(member.name)}
            </span>
            <span className="assignee-chip__name">{member.name}</span>
          </span>
        ))}
        {members.length === 0 ? <span className="cell-muted">участников нет</span> : null}
      </div>

      {queue.length === 0 ? (
        filtersActive ? null : (
          <p className="person-card__empty">Проектов в команде пока нет.</p>
        )
      ) : (
        <div className="table-wrap">
          <table className={controls.enabled ? 'data queue-table--manual' : 'data'}>
            <caption className="sr-only">Очередь команды {team.name}</caption>
            <thead>
              <tr>
                {controls.enabled ? <th scope="col" className="project-table__drag" aria-label="Ручной порядок" /> : null}
                <th scope="col">Приоритет команды</th>
                <th scope="col">Проект</th>
                <th scope="col">Исполнитель</th>
                <th scope="col">Статус</th>
                <th scope="col">Срок до</th>
                <th scope="col" className="num">
                  Часы
                </th>
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => {
                const drag = manualRowDragProps(row.id, controls, {
                  title: row.title,
                  statusLabel: statusLabel(registry.statuses, row.status),
                  priorityLabel: PROJECT_PRIORITY_LABELS[row.priority],
                });
                const isDragging = controls.dragId === row.id;
                const isDropTarget = controls.overId === row.id && controls.dragId !== row.id;
                const assignees = teamAssignees(row);
                return (
                  <tr
                    key={row.id}
                    aria-busy={patch.isPending || setTeamPositions.isPending || undefined}
                    className={[
                      'queue-row--clickable',
                      priorityRowClass(row),
                      isDragging ? 'project-row--dragging' : '',
                      isDropTarget ? 'project-row--drop-target' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    title="Открыть карточку проекта"
                    draggable={drag.draggable}
                    onDragStart={drag.onDragStart}
                    onDragOver={drag.onDragOver}
                    onDrop={drag.onDrop}
                    onDragEnd={drag.onDragEnd}
                    onClick={openFromRow(row)}
                  >
                    {controls.enabled ? (
                      <DragHandleCell id={row.id} label={row.title} controls={controls} busy={setTeamPositions.isPending} />
                    ) : null}
                    <td>
                      <select
                        className="input input--compact row-input"
                        aria-label={`Приоритет в очереди команды «${team.name}»: ${row.title}`}
                        value={teamPriorityOf(row, memberIds)}
                        disabled={patch.isPending}
                        onChange={(event) => setTeamPriority(row, event.target.value as Priority)}
                      >
                        {PROJECT_PRIORITIES.map((priority) => (
                          <option key={priority} value={priority}>
                            {PROJECT_PRIORITY_LABELS[priority]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="cell-primary">
                      <span className="cell-primary__row">
                        <span className="cell-primary__text" title={row.title}>
                          {row.title}
                        </span>
                        {/* The keyboard path to the same dialog (the row itself
                            is not focusable): a real button, STYLEGUIDE §7. */}
                        <button
                          type="button"
                          className="icon-btn icon-btn--sm cell-primary__edit"
                          aria-label={`Редактировать: ${row.title}`}
                          title="Открыть карточку проекта"
                          onClick={() => onEdit(row.id)}
                        >
                          <Pencil size={14} aria-hidden="true" />
                        </button>
                      </span>
                    </td>
                    {/* The team's OWN people on the project — both names when a
                        project is shared by two members of the same team. */}
                    <td className="team-row__assignees">
                      {assignees.length === 0 ? (
                        <span className="cell-muted">—</span>
                      ) : (
                        <span className="team-row__assignees-list">
                          {assignees.map((name) => (
                            <span key={name} className="assignee-chip" title={name}>
                              <span className="assignee-chip__avatar" aria-hidden="true">
                                {initials(name)}
                              </span>
                              <span className="assignee-chip__name">{name}</span>
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge>{statusLabel(registry.statuses, row.status)}</Badge>
                    </td>
                    <td>
                      <span className="date-cell">
                        {formatDisplayDate(row.dueDate) || '—'}
                        {isOverdue(row) ? <Badge tone="bad">просрочено</Badge> : null}
                      </span>
                    </td>
                    <td className="num">{row.hours ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The team's priority for a project = the minimal rank among its own assignments. */
function teamPriorityOf(project: Project, memberIds: ReadonlySet<string>): Priority {
  let best: Priority | undefined;
  let bestRank = Number.MAX_SAFE_INTEGER;
  for (const assignment of project.assignments) {
    if (!memberIds.has(assignment.personId)) continue;
    const rank = PROJECT_PRIORITIES.indexOf(assignment.teamPriority);
    if (rank < bestRank) {
      bestRank = rank;
      best = assignment.teamPriority;
    }
  }
  return best ?? 'low';
}
